import { useDispatch, useSelector } from 'react-redux';
import {
	useEffect, useRef, useMemo, useCallback, useState,
} from 'react';

import { SUB_TABS, TABS } from '../stores/reducers/session';
import { setActiveTab, setRightDrawerOpen } from '../stores/actions/session';
import { GreenroomState as GS } from '../stores/types/types';
import { setCurrentGraphicsId } from '../stores/actions/graphics';
import { setOpenContextMenuId } from '../stores/actions/user-interface';
import { setSelectedMediaTemplate } from '../stores/actions/generate-content';
import usePrevious from '../utils/use-previous';

export const useTabs = () => {
	const dispatch = useDispatch();

	const activeTab = useSelector((s: GS) => s.SessionReducer.activeTab);
	const previousTab = useSelector((s: GS) => s.SessionReducer.previousTab);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const selectedMedContent = useSelector((s: GS) => s.GeneratedContentReducer.selectedMediaItem);

	const selectedStylePackDrawer = useSelector(
		(s: GS) => s.GeneratedContentReducer.selectedStylePackDrawer,
	);

	const derenderingTabRef = useRef<string>();
	const derenderTimeoutRef = useRef<NodeJS.Timeout>();
	const [unRenderTrigger, setUnRenderTrigger] = useState(1);

	const validParentPrevTabRef = useRef(previousTab);
	useEffect(() => {
		if (SUB_TABS.indexOf(previousTab) === -1) {
			validParentPrevTabRef.current = previousTab;
		}
	}, [previousTab]);

	const prevTabIsValid = () => !!(validParentPrevTabRef?.current !== TABS.NONE);

	useEffect(() => {
		if (
			(!selectedMedContent && activeTab === TABS.GENERATE_MEDIA_CONTENT)
			|| (!currentGraphicId && activeTab === TABS.OVERLAYS)
		) {
			const hasValidPrevTab = prevTabIsValid();
			dispatch(setRightDrawerOpen(hasValidPrevTab));
			dispatch(setActiveTab(hasValidPrevTab ? validParentPrevTabRef.current : TABS.NONE));
		}
	}, [activeTab, selectedMedContent, currentGraphicId]);

	const checkPathClasses = (path: Array<any>) => {
		const len = path.length;
		let doKeepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				if (
					typeof path[i].className === 'string'
					&& (
						path[i].className.includes('graphic-wrapper')
						|| path[i].className.includes('graphic-menu-container')
						|| path[i].className.includes('graphic-drag-area')
						|| path[i].className.includes('right-drawer-button')
						|| path[i].className.includes('right-drawer-wrapper')
						|| path[i].className.includes('settings')
						|| path[i].className.includes('overlay')
						|| path[i].className.includes('tab')
						|| path[i].className.includes('right-drawer-wrapper')
						|| path[i].className.includes('moveable-custom-graphic-buttons')
						|| path[i].className.includes('showboard-drawer')
						|| path[i].className.includes('header-small-button')
						|| path[i].className.includes('audio-only-feeds-wrapper')
						|| path[i].className.includes('switch-wrapper')
						|| path[i].className.includes('duration-picker')
						|| path[i].className.includes('button')
						|| (
							path[i].className.includes('shot-thumbnail-inside')
							&& path[i].className.includes('selectable')
						)
					)
				) {
					doKeepOpen = true;
					break;
				}
			}
		}

		return doKeepOpen;
	};

	const checkPathIds = (path: Array<any>) => {
		let doKeepOpen = false;
		if (path.some((el: Element) => [
			'video-hover-options-portal',
			'video-hover-options-button',
			'controls-container-editing',
			'generate-content-media-editor',
		].includes(el.id))) {
			doKeepOpen = true;
		}
		return doKeepOpen;
	};

	const resetSubTabsState = () => {
		const validPrevTab = prevTabIsValid();
		dispatch(setRightDrawerOpen(validPrevTab));
		dispatch(setActiveTab(validPrevTab ? validParentPrevTabRef.current : TABS.NONE));
		dispatch(setCurrentGraphicsId(0));
		dispatch(setOpenContextMenuId(null));
		dispatch(setSelectedMediaTemplate(null, ''));
	};

	useEffect(() => {
		const handleClickOff = (e: any) => {
			const path = e.composedPath();

			const doKeepOpenBecauseClass = checkPathClasses(path);
			const doKeepOpenBecauseId = checkPathIds(path);

			if (!doKeepOpenBecauseClass && !doKeepOpenBecauseId) {
				resetSubTabsState();
			}
		};

		if (SUB_TABS.indexOf(activeTab) > -1) {
			window.removeEventListener('click', handleClickOff);
			window.addEventListener('click', handleClickOff);
		}

		return () => {
			window.removeEventListener('click', handleClickOff);
		};
	}, [activeTab]);

	const setDerenderTimeout = (oldActiveTab: string) => {
		derenderingTabRef.current = oldActiveTab;
		if (derenderTimeoutRef.current) clearTimeout(derenderTimeoutRef.current);
		derenderTimeoutRef.current = setTimeout(() => {
			derenderingTabRef.current = '';
			setUnRenderTrigger((prev) => prev + 1);
		}, 500);
	};

	const checkTabLabel = useCallback((tabLabel: string) => (
		activeTab === tabLabel
		|| derenderingTabRef.current === tabLabel
	), [activeTab, derenderingTabRef]);

	const shouldRender = useMemo(() => ({
		feeds: checkTabLabel(TABS.FEEDS),
		shots: checkTabLabel(TABS.SHOTS),
		media: checkTabLabel(TABS.MEDIA),
		notesTeleprompter: checkTabLabel(TABS.NOTES_TELEPROMPTER),
		// chat hard-renders (for listeners maybe?)
		chat: true,
		stylePacks: checkTabLabel(TABS.STYLE_PACKS) && selectedStylePackDrawer !== 'style-pack-info',
		stylePackInfo: checkTabLabel(TABS.STYLE_PACK_INFO) || selectedStylePackDrawer === 'style-pack-info',
		settings: checkTabLabel(TABS.SETTINGS),
		generateMediaContent: !!(checkTabLabel(TABS.GENERATE_MEDIA_CONTENT)),
		overlays: !!(checkTabLabel(TABS.OVERLAYS) || currentGraphicId),
		audience: checkTabLabel(TABS.AUDIENCE),
	}), [
		unRenderTrigger,
		checkTabLabel,
		selectedStylePackDrawer,
		currentGraphicId,
	]);

	const doHighlight = useMemo(() => {
		if (activeTab === TABS.OVERLAYS || activeTab === TABS.GENERATE_MEDIA_CONTENT) {
			return '';
		}

		return activeTab;
	}, [activeTab, validParentPrevTabRef]);

	return {
		derenderingTabRef,
		prevTabRef: validParentPrevTabRef,
		derenderTimeoutRef,
		setDerenderTimeout,
		shouldRender,
		doHighlight,
	};
};

export default useTabs;
